import { useSupabaseClient } from '~/composables/useSupabaseClient'
import type { DailyWorkFile } from '~/entities/dailyWorkFile'
import type { WorkDailyVerifier } from '~/entities/workDailyVerifier'
import type { WorkFileIdentification } from '~/entities/workFileIdentification'
import { type Dayjs, formatDate } from '~/utils/date'

export const getDailyWorkFilesByRange = async (
  checkFileId: DailyWorkFile['check_file_id'],
  startDate: Dayjs,
  endDate: Dayjs
) => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_sheets(*, work_items(*, notes(*), tickets(*), work_item_corrections(*))), work_daily_verifiers(*)')
    .eq('check_file_id', checkFileId)
    .gte('work_date', formatDate(startDate))
    .lte('work_date', formatDate(endDate))

  if (error) throw error

  return data
}

export const getDailyWorkFilesAndVerifiers = async ({
  dailyWorkFile,
  workDailyVerifier,
  limit,
}: {
  dailyWorkFile: {
    status: DailyWorkFile['status']
    verified: DailyWorkFile['verified']
    locationId: DailyWorkFile['location_id']
  }
  workDailyVerifier: {
    user_id: WorkDailyVerifier['user_id']
    status: WorkDailyVerifier['status']
  }
  limit?: number
}) => {
  const query = useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_daily_verifiers!inner(*), work_sheets(*, work_items(*, notes(*), tickets(*)))')
    .eq('work_daily_verifiers.user_id', workDailyVerifier.user_id)
    .eq('work_daily_verifiers.status', workDailyVerifier.status)
    .eq('status', dailyWorkFile.status)
    .eq('verified', dailyWorkFile.verified)
    .eq('location_id', dailyWorkFile.locationId)
    .order('work_date', { ascending: true })
    .order('check_file_title', { ascending: true })

  if (limit) {
    query.limit(limit)
  }

  const { data, error } = await query

  if (error) throw error

  return data
}

export const getDailyWorkFilesHavingSharedMatters = async ({
  checkFileIds,
}: {
  checkFileIds: DailyWorkFile['check_file_id'][]
}): Promise<DailyWorkFile[]> => {
  const { data: havingNotes, error: havingNotesError } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_sheets!inner(*, work_items!inner(*, notes!inner(*)))')
    .in('check_file_id', checkFileIds)

  if (havingNotesError) throw havingNotesError

  const { data: havingTickets, error: havingTicketsError } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_sheets!inner(*, work_items!inner(*,  tickets!inner(*)))')
    .in('check_file_id', checkFileIds)

  if (havingTicketsError) throw havingTicketsError

  const havingNotesIdSet = new Set(havingNotes.map((file) => file.id))

  // 重複を除外しつつ、結合して返す
  return [...havingNotes, ...havingTickets.filter((file) => !havingNotesIdSet.has(file.id))]
}

export const getDailyWorkFile = async (dailyWorkFileId: number) => {
  const query = useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_file_identifications(*)')
    .eq('id', dailyWorkFileId)
    .single()

  const { data, error } = await query

  if (error) throw error

  return data
}

export const getWorkItem = async (workItemId: number) => {
  const query = useSupabaseClient()
    .from('work_items')
    .select(
      '*, work_sheets(*, daily_work_files(*)), notes(*), tickets(*, ticket_images(*, images(*))), work_number_conditions(value, comparison_operator), work_item_corrections(*)'
    )
    .eq('id', workItemId)
    .single()

  const { data, error } = await query

  if (error) throw error

  return data
}

export const getLatestDailyWorkFile = async (
  checkFileId: number,
  startDate: Dayjs,
  endDate: Dayjs
): Promise<DailyWorkFile | null> => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*')
    .eq('check_file_id', checkFileId)
    .gte('work_date', formatDate(startDate))
    .lte('work_date', formatDate(endDate))
    .order('work_date', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error

  return data
}

export const getLatestAsNeededWorkFile = async (
  checkFileId: number
): Promise<(DailyWorkFile & { work_file_identifications: WorkFileIdentification[] }) | null> => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('*, work_file_identifications(*)')
    .eq('check_file_id', checkFileId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) throw error

  return data
}

export const getAsNeededWorkFile = async (workFileId: DailyWorkFile['id']) => {
  const { data, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select(
      '*, work_sheets(*, work_items(*, notes(*), tickets(*), work_item_corrections(*))), work_daily_verifiers(*), work_file_identifications(*)'
    )
    .eq('id', workFileId)
    .eq('check_file_type', 'AS_NEEDED')
    .single()

  if (error) throw error

  return data
}

export const existsDailyWorkFile = async (checkFileId: DailyWorkFile['check_file_id']): Promise<boolean> => {
  const { count, error } = await useSupabaseClient()
    .from('daily_work_files')
    .select('id', { count: 'exact', head: true })
    .eq('check_file_id', checkFileId)

  if (error) throw error

  return (count ?? 0) > 0
}
